import {
  ElementStatus,
  PageStructure,
  SelectedElementContainer,
  ToolMessage,
  useMove,
  useUpdateBounds,
  ViewElementContainer
} from '@st/document-viewer-kit'
import type { NewAnnotation } from '@st/document-viewer-kit/plugins/annotations'
import { move, Offset, Point, rectFromPoints } from '@st/geom'
import { useKeyboardShortcuts } from '@st/react-util/shortcuts'
import { Rect, RectangleAnnotation } from '@st/sdk'
import { useState } from 'react'
import { match } from '@st/pattern'
import { DeleteIcon } from '~/components/icons'
import { DocumentAnnotationDisplay } from '~/st-doc-viewer/document-annotation-display'
import { RectToolState } from '../../annotations'
import { AnnotationFloatingToolbar, FloatingToolbarButton } from '../../floating-toolbar'
import { InlineColorPicker } from '../../inline-color-picker'
import { copyToolState, RECT_COLOR_OPTIONS } from './tool-state'
import {
  constrainPageRectMove,
  constrainPageRectResize
} from '~/st-doc-viewer/page-annotation-bounds'

export function RectangleToolEditor({
  toolState,
  activeAnnotation,
  send
}: {
  toolState: RectToolState
  activeAnnotation: RectangleAnnotation | undefined
  send: (message: ToolMessage<RectangleAnnotation, RectToolState>) => void
}) {
  return (
    <InlineColorPicker
      options={RECT_COLOR_OPTIONS}
      value={toolState.settings.border.color}
      onChange={(color) => {
        if (activeAnnotation) {
          const newAnnotation: RectangleAnnotation = {
            ...activeAnnotation,
            border: { ...activeAnnotation.border, color: color }
          }
          send({
            type: 'updateAnnotationDraft',
            annotationDraft: newAnnotation
          })
          send({ type: 'updateTool', tool: copyToolState(newAnnotation) })
        } else {
          send({
            type: 'updateTool',
            tool: {
              ...toolState,
              settings: {
                ...toolState.settings,
                border: { ...toolState.settings.border, color: color }
              }
            }
          })
        }
      }}
    />
  )
}

type CreateRectState =
  | { status: 'idle' }
  | {
      status: 'drawing'
      initialPoint: Point
      draggingPoint: Point
      annotation: NewAnnotation<RectangleAnnotation>
    }

export function RectangleToolOverlay({
  page,
  toolState,
  activeAnnotation,
  status,
  send
}: {
  page: PageStructure
  toolState: RectToolState
  activeAnnotation: RectangleAnnotation | undefined
  status: ElementStatus
  send: (message: ToolMessage<RectangleAnnotation, RectToolState>) => void
}) {
  const [state, setState] = useState<CreateRectState>({ status: 'idle' })

  const { ref, onPointerDown, onPointerMove, onPointerUp } = useMove({
    canvasClass: 'drawing-canvas',
    onMove: (e) => {
      switch (e.type) {
        case 'start':
          setState({
            status: 'drawing',
            initialPoint: e.start.point,
            draggingPoint: e.start.point,
            annotation: {
              type: 'rectangle',
              pageNumber: page.pageNumber,
              bounds: {
                x: e.start.point.x,
                y: e.start.point.y,
                width: 0,
                height: 0
              },
              border: toolState.settings.border
            }
          })
          break
        case 'move':
        case 'end':
          if (state.status !== 'drawing') return state

          const updatedAnnotation: NewAnnotation<RectangleAnnotation> = {
            ...state.annotation,
            bounds: constrainPageRectResize(
              rectFromPoints(state.initialPoint, e.current.point),
              page.bounds
            )
          }

          setState({
            status: 'drawing',
            initialPoint: state.initialPoint,
            draggingPoint: e.current.point,
            annotation: updatedAnnotation
          })

          if (e.type === 'end') {
            if (updatedAnnotation.bounds.width > 10 || updatedAnnotation.bounds.height > 10) {
              send({
                type: 'requestAnnotationCreation',
                annotationDraft: updatedAnnotation
              })
            }
            setState({ status: 'idle' })
          }
          break
      }
    }
  })

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        cursor: 'crosshair',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.0)'
      }}
      onPointerDown={(e) => {
        if (activeAnnotation) {
          send({ type: 'deselectAnnotation' })
        } else {
          onPointerDown(e)
        }
      }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {state.status === 'drawing' && (
        <ViewElementContainer bounds={state.annotation.bounds}>
          <RectangleAnnotationDisplay annotation={state.annotation} />
        </ViewElementContainer>
      )}
    </div>
  )
}

export function RectangleAnnotationElement({
  annotation,
  page,
  status,
  send
}: {
  annotation: RectangleAnnotation
  page: PageStructure
  status: ElementStatus
  send: (message: ToolMessage<RectangleAnnotation, RectToolState>) => void
}) {
  if (status == 'editing' || status == 'selected') {
    return <RectangleAnnotationEdit annotation={annotation} page={page} send={send} />
  }
  return <RectangleAnnotationView annotation={annotation} send={send} />
}

function RectangleAnnotationView({
  annotation,
  send
}: {
  annotation: RectangleAnnotation
  send: (message: ToolMessage<RectangleAnnotation, RectToolState>) => void
}) {
  return (
    <ViewElementContainer
      key={annotation.id}
      bounds={annotation.bounds}
      onClick={() => send({ type: 'selectAnnotation', annotationId: annotation.id })}
    >
      <RectangleAnnotationDisplay annotation={annotation} />
    </ViewElementContainer>
  )
}

function RectangleAnnotationEdit({
  annotation,
  page,
  send
}: {
  annotation: RectangleAnnotation
  page: PageStructure
  send: (message: ToolMessage<RectangleAnnotation, RectToolState>) => void
}) {
  function handleNudge(offset: Partial<Offset>) {
    send({
      type: 'updateAnnotationDraft',
      annotationDraft: {
        ...annotation,
        bounds: constrainPageRectMove(move(annotation.bounds, offset), page.bounds)
      }
    })
  }

  useKeyboardShortcuts({
    Backspace: () => {
      send({ type: 'deleteAnnotation', annotationId: annotation.id })
    },
    ArrowUp: () => handleNudge({ dy: -5 }),
    ArrowDown: () => handleNudge({ dy: 5 }),
    ArrowLeft: () => handleNudge({ dx: -5 }),
    ArrowRight: () => handleNudge({ dx: 5 })
  })

  const { bounds, onMove, onResize } = useUpdateBounds({
    initialBounds: annotation.bounds,
    constrain: (event) =>
      match(event.type)
        .with('move', () => constrainPageRectMove(event.newBounds, page.bounds))
        .with('resize', () => constrainPageRectResize(event.newBounds, page.bounds))
        .exhaustive(),
    onCommit: (event) => {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: event.newBounds }
      })
    }
  })

  return (
    <SelectedElementContainer
      key={annotation.id}
      bounds={bounds}
      onMove={onMove}
      onResize={onResize}
      resizable={true}
      toolbar={
        <AnnotationFloatingToolbar>
          <FloatingToolbarButton
            icon={<DeleteIcon className="size-6" />}
            onClick={() => send({ type: 'deleteAnnotation', annotationId: annotation.id })}
          />
        </AnnotationFloatingToolbar>
      }
    >
      <RectangleAnnotationDisplay annotation={{ ...annotation, bounds }} />
    </SelectedElementContainer>
  )
}

function RectangleAnnotationDisplay({
  annotation
}: {
  annotation: NewAnnotation<RectangleAnnotation> | RectangleAnnotation
}) {
  return <DocumentAnnotationDisplay annotation={annotation} />
}
